/**
 * CL-408F — Resolve signed-in Auth user to portal profile (users/{uid}).
 * Bootstraps users/{auth.uid} from CRM contact + email-keyed projectAssignments when missing.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import {
  buildAllowedUserTypesForInviteRole,
  buildConstructionRolesFromInvite,
  mergeAllowedUserTypesForCollaborator,
} from "./portalInvite";
import { applyCrmContactAuthUidLink } from "./inviteSignupIdentityLink";

function safeStr(value) {
  return String(value ?? "").trim();
}

export function normalizePortalEmail(value) {
  return safeStr(value).toLowerCase();
}

function uniqueStrings(values) {
  return Array.from(new Set((Array.isArray(values) ? values : []).map((v) => safeStr(v)).filter(Boolean)));
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function normalizeAssignmentStatus(row) {
  return safeStr(row?.status || "active").toLowerCase();
}

function isActiveAssignment(row) {
  return normalizeAssignmentStatus(row) !== "removed" && normalizeAssignmentStatus(row) !== "inactive";
}

/** CRM row eligible for email-based auth linking (not the auth-canonical users/{uid} doc). */
export function findLinkableCrmContactByEmail(userRows, emailLower, authUid) {
  const em = normalizePortalEmail(emailLower);
  const uid = safeStr(authUid);
  if (!em) return null;
  const matches = (Array.isArray(userRows) ? userRows : []).filter((row) => {
    const rowEmail = normalizePortalEmail(row?.emailLower || row?.email);
    if (rowEmail !== em) return false;
    if (safeStr(row?.id) === uid) return false;
    if (safeStr(row?.status).toLowerCase() === "archived") return false;
    const linkedUid = safeStr(row?.authUid);
    if (linkedUid && linkedUid !== uid) return false;
    return true;
  });
  if (!matches.length) return null;
  if (matches.length === 1) return matches[0];
  const crmOnly = matches.find((row) => !safeStr(row?.authUid));
  if (crmOnly) return crmOnly;
  const notAuthDoc = matches.find((row) => safeStr(row.id) !== uid);
  return notAuthDoc || matches[0];
}

/**
 * Whether the signed-in user should get portal access without an existing users/{uid} doc.
 */
export function hasPortalAccessFromSignals({ assignments = [], crmContact = null, crmRows = [] } = {}) {
  const activeAssignments = (assignments || []).filter(isActiveAssignment);
  if (activeAssignments.length > 0) return true;

  if (crmContact?.portalEnabled === true) return true;
  if (uniqueStrings(crmContact?.assignedProjectIds).length > 0) return true;

  const contacts = [];
  const seen = new Set();
  [...(crmContact ? [crmContact] : []), ...(crmRows || [])].forEach((row) => {
    const key = safeStr(row?.id) || normalizePortalEmail(row?.emailLower || row?.email);
    if (!key || seen.has(key)) return;
    seen.add(key);
    contacts.push(row);
  });

  for (const contact of contacts) {
    if (contact?.portalEnabled === true) return true;
    if (uniqueStrings(contact?.assignedProjectIds).length > 0) return true;
  }

  const onlyArchived =
    contacts.length > 0 &&
    contacts.every((row) => safeStr(row?.status).toLowerCase() === "archived");
  if (onlyArchived) return false;

  return false;
}

export function buildBootstrapPortalProfile({
  uid,
  emailLower,
  crmContact = null,
  assignments = [],
  authDisplayName = "",
} = {}) {
  const em = normalizePortalEmail(emailLower);
  const active = (assignments || []).filter(isActiveAssignment);
  const projectIds = uniqueStrings([
    ...active.map((row) => row.projectId),
    ...(Array.isArray(crmContact?.assignedProjectIds) ? crmContact.assignedProjectIds : []),
  ]);
  const accessScopes = uniqueStrings([
    ...active.map((row) => row.accessScope),
    ...(Array.isArray(crmContact?.accessScopes) ? crmContact.accessScopes : []),
  ]);
  const clientRow = active.find((row) => safeStr(row.role).toLowerCase() === "client");
  const primaryRole =
    safeStr(crmContact?.primaryUserType || crmContact?.role).toLowerCase() ||
    safeStr(clientRow?.role).toLowerCase() ||
    safeStr(active[0]?.role).toLowerCase() ||
    "client";
  const services = uniqueStrings(
    Array.isArray(crmContact?.services) && crmContact.services.length
      ? crmContact.services
      : ["construction"]
  );
  let clientType = safeStr(crmContact?.clientType).toLowerCase() || "construction";
  if (services.includes("realEstate") && services.includes("construction")) clientType = "both";
  else if (services.includes("realEstate")) clientType = "realEstate";

  const displayName =
    safeStr(crmContact?.displayName) ||
    safeStr(active[0]?.displayName) ||
    safeStr(authDisplayName) ||
    em.split("@")[0] ||
    "Portal user";

  const constructionRoles = uniqueStrings([
    ...buildConstructionRolesFromInvite(primaryRole),
    ...(Array.isArray(crmContact?.constructionRoles) ? crmContact.constructionRoles : []),
    ...active.map((row) => row.role),
  ]);

  return {
    authUid: uid,
    email: em,
    emailLower: em,
    firstName: safeStr(crmContact?.firstName),
    lastName: safeStr(crmContact?.lastName),
    displayName,
    phone: safeStr(crmContact?.phone),
    preferredContact: safeStr(crmContact?.preferredContact) || "email",
    services,
    clientType,
    role: primaryRole === "client" ? "client" : "construction_collaborator",
    primaryUserType: primaryRole,
    allowedUserTypes: crmContact
      ? mergeAllowedUserTypesForCollaborator(
          crmContact.allowedUserTypes || crmContact.userTypes || [],
          primaryRole
        )
      : buildAllowedUserTypesForInviteRole(primaryRole, services),
    constructionRoles,
    assignedProjectIds: projectIds,
    accessScopes,
    portalEnabled: crmContact?.portalEnabled === true || active.length > 0,
    realEstatePortalAccess:
      crmContact?.realEstatePortalAccess === true || services.includes("realEstate"),
    status: "active",
    source: "portal_account_link",
    linkedCrmContactId: safeStr(crmContact?.id) || null,
  };
}

export async function loadEmailKeyedAssignments(db, emailLower) {
  const em = normalizePortalEmail(emailLower);
  if (!db || !em) return [];
  try {
    const snap = await getDocs(
      query(collection(db, "projectAssignments"), where("emailLower", "==", em), limit(100))
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn("clientPortalProfileResolve: projectAssignments email query failed:", err);
    return [];
  }
}

export async function loadUidKeyedAssignments(db, uid) {
  const authUid = safeStr(uid);
  if (!db || !authUid) return [];
  try {
    const snap = await getDocs(
      query(collection(db, "projectAssignments"), where("userId", "==", authUid), limit(100))
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn("clientPortalProfileResolve: projectAssignments uid query failed:", err);
    return [];
  }
}

async function loadEmailKeyedAssignmentsStrict(db, emailLower) {
  const em = normalizePortalEmail(emailLower);
  if (!db || !em) return [];
  const snap = await getDocs(
    query(collection(db, "projectAssignments"), where("emailLower", "==", em), limit(100))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

async function loadUidKeyedAssignmentsStrict(db, uid) {
  const authUid = safeStr(uid);
  if (!db || !authUid) return [];
  const snap = await getDocs(
    query(collection(db, "projectAssignments"), where("userId", "==", authUid), limit(100))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Portal project load must surface permission/query failures instead of empty lists. */
export async function loadPortalAssignmentsForUserStrict(db, { uid, emailLower }) {
  const merged = new Map();
  const errors = [];
  const tasks = [];

  if (safeStr(uid)) {
    tasks.push(
      loadUidKeyedAssignmentsStrict(db, uid)
        .then((rows) => {
          rows.forEach((row) => {
            if (row?.id) merged.set(row.id, row);
          });
        })
        .catch((err) => {
          errors.push(err);
        })
    );
  }

  const em = normalizePortalEmail(emailLower);
  if (em) {
    tasks.push(
      loadEmailKeyedAssignmentsStrict(db, em)
        .then((rows) => {
          rows.forEach((row) => {
            if (row?.id) merged.set(row.id, row);
          });
        })
        .catch((err) => {
          errors.push(err);
        })
    );
  }

  await Promise.all(tasks);
  if (!merged.size && errors.length) {
    throw errors[0];
  }
  return Array.from(merged.values());
}

export async function loadPortalAssignmentsForUser(db, { uid, emailLower }) {
  const merged = new Map();
  const [byEmail, byUid] = await Promise.all([
    loadEmailKeyedAssignments(db, emailLower),
    loadUidKeyedAssignments(db, uid),
  ]);
  [...byEmail, ...byUid].forEach((row) => {
    if (row?.id) merged.set(row.id, row);
  });
  return Array.from(merged.values());
}

export async function loadCrmContactsByEmail(db, emailLower) {
  const em = normalizePortalEmail(emailLower);
  if (!db || !em) return [];
  try {
    const snap = await getDocs(query(collection(db, "users"), where("emailLower", "==", em), limit(5)));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn("clientPortalProfileResolve: CRM email lookup failed:", err);
    return [];
  }
}

/** Link email-keyed assignment rows to Auth uid (signup + returning login). */
function buildUidKeyedAssignmentFromEmailDoc(data, { projectId, authUid, emailLower, sourceAssignmentId }) {
  return {
    projectId,
    userId: authUid,
    email: data.email || null,
    emailLower: data.emailLower || emailLower || null,
    displayName: data.displayName || null,
    company: data.company || null,
    role: data.role || "reviewer",
    accessScope: data.accessScope || "full_project",
    status: data.status || "active",
    permissions: data.permissions || {},
    portalEnabled: data.portalEnabled !== false,
    linkedFromAssignmentId: sourceAssignmentId || null,
    updatedAt: serverTimestamp(),
    updatedBy: authUid,
  };
}

export async function linkEmailKeyedAssignmentsToUid(db, { emailLower, uid, restrictProjectIds = null }) {
  const em = normalizePortalEmail(emailLower);
  const authUid = safeStr(uid);
  if (!db || !em || !authUid) return [];

  const restrict = Array.isArray(restrictProjectIds) && restrictProjectIds.length > 0;
  const allowed = new Set(uniqueStrings(restrictProjectIds));

  const snap = await getDocs(
    query(collection(db, "projectAssignments"), where("emailLower", "==", em), limit(100))
  );
  const batch = writeBatch(db);
  let writes = 0;
  const linkedProjectIds = [];

  for (const d of snap.docs) {
    if (writes >= 450) break;
    const data = d.data() || {};
    const pid = safeStr(data.projectId);
    if (!pid) continue;
    if (restrict && !allowed.has(pid)) continue;

    const uidAssignmentId = `${pid}__${authUid}`;
    const uidAssignmentRef = doc(db, "projectAssignments", uidAssignmentId);
    const uidPatch = buildUidKeyedAssignmentFromEmailDoc(data, {
      projectId: pid,
      authUid,
      emailLower: em,
      sourceAssignmentId: d.id,
    });

    const existingUserId = safeStr(data.userId);
    if (existingUserId === authUid) {
      if (d.id !== uidAssignmentId) {
        batch.set(uidAssignmentRef, uidPatch, { merge: true });
        writes += 1;
      }
      linkedProjectIds.push(pid);
      continue;
    }

    batch.update(d.ref, {
      userId: authUid,
      updatedAt: serverTimestamp(),
      updatedBy: authUid,
    });
    batch.set(uidAssignmentRef, uidPatch, { merge: true });
    linkedProjectIds.push(pid);
    writes += 2;
  }

  if (writes) await batch.commit();
  return uniqueStrings(linkedProjectIds);
}

/** Backfill sharedWithUid on email-keyed file shares after account link. */
export async function linkEmailKeyedFileSharesToUid(db, { emailLower, uid, projectIds = [] }) {
  const em = normalizePortalEmail(emailLower);
  const authUid = safeStr(uid);
  const pidSet = new Set(uniqueStrings(projectIds));
  if (!db || !em || !authUid || !pidSet.size) return 0;

  let updated = 0;
  const snap = await getDocs(
    query(collection(db, "projectFileShares"), where("sharedWithEmail", "==", em), limit(500))
  );
  let batch = writeBatch(db);
  let n = 0;

  for (const d of snap.docs) {
    const data = d.data() || {};
    const pid = safeStr(data.projectId);
    if (!pidSet.has(pid)) continue;
    if (safeStr(data.sharedWithUid)) continue;
    batch.update(d.ref, {
      sharedWithUid: authUid,
      updatedAt: serverTimestamp(),
    });
    updated += 1;
    n += 1;
    if (n >= 400) {
      await batch.commit();
      batch = writeBatch(db);
      n = 0;
    }
  }
  if (n) await batch.commit();
  return updated;
}

async function linkCrmContactAuthUid(db, crmContact, uid) {
  const result = await applyCrmContactAuthUidLink(
    db,
    crmContact?.id,
    uid,
    crmContact?.authUid
  );
  return result.linkCreated;
}

async function loadProfileDoc(db, uid) {
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? snap.data() : null;
}

/**
 * Resolve portal profile for signed-in user.
 * @returns {Promise<{ profile: object|null, source: string, reason?: string }>}
 */
export async function resolveClientPortalProfile(db, authUser, options = {}) {
  const uid = safeStr(authUser?.uid);
  const emailLower = normalizePortalEmail(authUser?.email);
  const maxAttempts = Number(options.maxAttempts) || 6;
  const delayMs = Number(options.delayMs) || 350;

  if (!uid || authUser?.isAnonymous) {
    return { profile: null, source: "none", reason: "anonymous" };
  }
  if (!emailLower) {
    return { profile: null, source: "none", reason: "missing_email" };
  }

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const direct = await loadProfileDoc(db, uid);
    if (direct) return { profile: direct, source: "auth_uid" };
    if (attempt < maxAttempts) await sleep(delayMs);
  }

  const [assignments, crmRows] = await Promise.all([
    loadPortalAssignmentsForUser(db, { uid, emailLower }),
    loadCrmContactsByEmail(db, emailLower),
  ]);
  const crmContact = findLinkableCrmContactByEmail(crmRows, emailLower, uid);

  if (!hasPortalAccessFromSignals({ assignments, crmContact, crmRows })) {
    return { profile: null, source: "none", reason: "no_access" };
  }

  const linkedProjectIds = await linkEmailKeyedAssignmentsToUid(db, { emailLower, uid });
  await linkCrmContactAuthUid(db, crmContact, uid);

  const bootstrap = buildBootstrapPortalProfile({
    uid,
    emailLower,
    crmContact,
    assignments,
    authDisplayName: safeStr(authUser?.displayName),
  });
  const mergedProjectIds = uniqueStrings([
    ...bootstrap.assignedProjectIds,
    ...linkedProjectIds,
  ]);

  await setDoc(
    doc(db, "users", uid),
    {
      ...bootstrap,
      assignedProjectIds: mergedProjectIds,
      authUid: uid,
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    },
    { merge: true }
  );

  await linkEmailKeyedFileSharesToUid(db, {
    emailLower,
    uid,
    projectIds: mergedProjectIds,
  });

  const profile = await loadProfileDoc(db, uid);
  if (!profile) {
    return { profile: null, source: "none", reason: "bootstrap_failed" };
  }
  return { profile, source: "account_link" };
}

export function portalSetupPendingMessage(reason) {
  switch (safeStr(reason).toLowerCase()) {
    case "no_access":
      return "No portal access was found for this account. If you were invited to a project, sign in with the same email your team used, or contact your project administrator.";
    case "missing_email":
      return "Your login is missing a verified email address. Sign out, then sign in again with email and password, or contact admin.";
    case "bootstrap_failed":
      return "We could not finish setting up your portal profile. Try refreshing, or contact admin if this continues.";
    default:
      return "Profile setup pending — contact admin.";
  }
}
